import React, { useCallback } from 'react';
import useDraftBatchStore from '@/stores/draftBatchStore';

/**
 * DraftReviewPanel — shows draft items for a specific batch with commit/discard controls.
 * Renders inline within the chat message flow.
 */
export default function DraftReviewPanel({ batchId }) {
  const batch = useDraftBatchStore((s) => s.batches[batchId]);
  const itemStatuses = useDraftBatchStore((s) => s.itemStatuses);
  const commitBatch = useDraftBatchStore((s) => s.commitBatch);
  const discardBatch = useDraftBatchStore((s) => s.discardBatch);
  const commitItem = useDraftBatchStore((s) => s.commitItem);
  const discardItem = useDraftBatchStore((s) => s.discardItem);

  const handleCommitAll = useCallback(() => {
    commitBatch(batchId).catch(() => {});
  }, [batchId, commitBatch]);

  const handleDiscardAll = useCallback(() => {
    discardBatch(batchId).catch(() => {});
  }, [batchId, discardBatch]);

  if (!batch || batch.items.length === 0) return null;

  const isProcessing = batch.status === 'committing';
  const isFinished = batch.status === 'committed' || batch.status === 'discarded';

  return (
    <div className="rounded-lg border border-base-300 bg-base-200/50 p-3 mt-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-base-content/70">
            {batch.items.length} draft{batch.items.length !== 1 ? 's' : ''} pending review
          </span>
          {isFinished && (
            <span className={`badge badge-xs ${batch.status === 'committed' ? 'badge-success' : 'badge-ghost'}`}>
              {batch.status === 'committed' ? 'Committed' : 'Discarded'}
            </span>
          )}
        </div>

        {/* Batch actions */}
        {!isFinished && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleDiscardAll}
              disabled={isProcessing}
              className="btn btn-ghost btn-xs text-error hover:bg-error/10"
            >
              Discard All
            </button>
            <button
              onClick={handleCommitAll}
              disabled={isProcessing}
              className="btn btn-primary btn-xs"
            >
              {isProcessing ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                'Commit All'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="space-y-1.5">
        {batch.items.map((item) => {
          const key = `${item.collection}/${item.docId}`;
          const status = itemStatuses[key] || 'pending';

          return (
            <DraftItem
              key={key}
              item={item}
              status={status}
              onCommit={() => commitItem(item.collection, item.docId).catch(() => {})}
              onDiscard={() => discardItem(item.collection, item.docId).catch(() => {})}
              batchFinished={isFinished}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Single draft item row */
function DraftItem({ item, status, onCommit, onDiscard, batchFinished }) {
  const isPending = status === 'pending';
  const isCommitting = status === 'committing';

  // Extract a readable label from collection path
  const collectionLabel = item.collection
    ?.split('/')
    .pop()
    ?.replace(/([A-Z])/g, ' $1')
    .trim() || 'item';

  return (
    <div className="flex items-center justify-between gap-2 rounded bg-base-100 px-2.5 py-1.5 text-xs">
      <div className="flex items-center gap-2 min-w-0">
        {/* Status indicator */}
        {status === 'committed' && (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-success shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
        {status === 'discarded' && (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-base-content/30 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
        {isCommitting && (
          <span className="loading loading-spinner loading-xs shrink-0" />
        )}
        {isPending && (
          <span className="w-2 h-2 rounded-full bg-warning shrink-0" />
        )}

        {/* Label */}
        <span className={`truncate ${status === 'discarded' ? 'line-through text-base-content/30' : 'text-base-content/70'}`}>
          {item.summary || `${collectionLabel}: ${item.docId?.slice(0, 8)}...`}
        </span>
      </div>

      {/* Per-item actions */}
      {isPending && !batchFinished && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onDiscard}
            className="btn btn-ghost btn-xs px-1.5 text-error/70 hover:text-error hover:bg-error/10"
            title="Discard"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          <button
            onClick={onCommit}
            className="btn btn-ghost btn-xs px-1.5 text-success/70 hover:text-success hover:bg-success/10"
            title="Commit"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
